import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import useScreen from 'hooks/useScreen';
import { useWindowSize } from 'react-use';
import convertImgixPropsToQueryParams from 'commons/convertImgixPropsToQueryParams';
import useVideoMetadata from 'hooks/useVideoMetadata';
import { getMainImageImgixPropsForPhoto } from '../PDPDesktopHeroGallery/helpers/imgix-props';
import PDPDesktopFullScreenGallery2UI from './PDPDesktopFullScreenGallery2UI';
import { galleryCommonImgixProps, GALLERY_IMAGE_WIDTH, GALLERY_IMAGE_WIDTH_ZOOM } from './galleryCommonImgixProps';
const PDPDesktopFullScreenGallery2 = ({
  isEditOrder = false,
  enableCrops = true,
  initialSelectedPhotoIndex = 0,
  introVideoId = null,
  isOpen = false,
  onClose,
  photos = [],
  productName = ''
}) => {
  const {
    isMobileOrTablet
  } = useScreen();
  const {
    width: windowWidth
  } = useWindowSize();
  const galleryImageWidth = windowWidth >= 1640 ? 1800 : GALLERY_IMAGE_WIDTH;
  const [isVideoSelected, setVideoSelected] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(initialSelectedPhotoIndex);
  const {
    thumbnail: videoThumbnailUrl
  } = useVideoMetadata({
    videoId: introVideoId
  });
  const getImageUrl = useCallback(({
    index,
    isZoomed
  }) => convertImgixPropsToQueryParams({
    ...getMainImageImgixPropsForPhoto(photos[index], true),
    ...galleryCommonImgixProps,
    width: isZoomed ? GALLERY_IMAGE_WIDTH_ZOOM : galleryImageWidth
  }, photos[index].src, isMobileOrTablet), [photos, isMobileOrTablet, galleryImageWidth]);
  const [photoSrc, setPhotoSrc] = useState(photos?.length && photos?.[initialSelectedPhotoIndex]?.src ? {
    zoomSrc: getImageUrl({
      index: initialSelectedPhotoIndex,
      isZoomed: true
    }),
    src: getImageUrl({
      index: initialSelectedPhotoIndex,
      isZoomed: false
    })
  } : '');
  const handleClose = () => {
    onClose();
  };
  const handleVideoSelected = useCallback(updatedIsVideoSelected => {
    if (updatedIsVideoSelected !== isVideoSelected) {
      setVideoSelected(updatedIsVideoSelected);
    }
  }, [isVideoSelected]);
  useEffect(() => {
    if (initialSelectedPhotoIndex !== selectedPhotoIndex) {
      setSelectedPhotoIndex(initialSelectedPhotoIndex);
    }
    // Disable exhaustive dependencies rule here, because we only want the
    // selected photo to be updated by this hook if the
    // initialSelectedPhotoIndex (passed in via prop) is changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedPhotoIndex]);
  useEffect(() => {
    if (!photos?.length || !photos?.[selectedPhotoIndex] || !isOpen) {
      return;
    }
    const updatedZoomSrc = getImageUrl({
      index: selectedPhotoIndex,
      isZoomed: true
    });
    const updatedSrc = getImageUrl({
      index: selectedPhotoIndex,
      isZoomed: false
    });
    if (updatedSrc !== photoSrc?.src) {
      setPhotoSrc({
        zoomSrc: updatedZoomSrc,
        src: updatedSrc
      });
    }
  }, [getImageUrl, isOpen, photos, photoSrc?.src, selectedPhotoIndex]);
  if (!photos?.length) {
    return <div className="PDPDesktopFullScreenGallery2__filler" />;
  }
  return <PDPDesktopFullScreenGallery2UI isEditOrder={isEditOrder} enableCrops={enableCrops} handleVideoSelected={handleVideoSelected} introVideoId={introVideoId} isOpen={isOpen} isVideoSelected={isVideoSelected} onClose={handleClose} photos={photos} photoSrc={photoSrc} productName={productName} selectedPhotoIndex={selectedPhotoIndex} setSelectedPhotoIndex={setSelectedPhotoIndex} videoThumbnailUrl={videoThumbnailUrl} />;
};
PDPDesktopFullScreenGallery2.propTypes = {
  isEditOrder: PropTypes.bool,
  enableCrops: PropTypes.bool,
  initialSelectedPhotoIndex: PropTypes.number,
  introVideoId: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  photos: PropTypes.arrayOf(PropTypes.shape({
    alt: PropTypes.string,
    src: PropTypes.string
  })),
  productName: PropTypes.string
};
export default PDPDesktopFullScreenGallery2;